import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle, RefreshCw, Smartphone, Wifi } from 'lucide-react';
import { getApiEndpoint } from '../../utils/apiUrl';

type QRStatus = 'idle' | 'starting' | 'qr' | 'connected' | 'disconnected' | 'error';

interface WhatsAppQRConnectProps {
  onConnected: (phone: string, displayName: string) => void;
}

export default function WhatsAppQRConnect({ onConnected }: WhatsAppQRConnectProps) {
  const [status, setStatus] = useState<QRStatus>('idle');
  const [qrImage, setQrImage] = useState<string | null>(null);
  const [phone, setPhone] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const esRef = useRef<EventSource | null>(null);

  const startConnection = () => {
    // Fermer toute connexion SSE existante
    esRef.current?.close();

    setStatus('starting');
    setQrImage(null);
    setErrorMsg('');

    const token = localStorage.getItem('token');
    if (!token) { setStatus('error'); setErrorMsg('Non authentifié'); return; }

    const url = getApiEndpoint(`/api/whatsapp/qr/connect?token=${encodeURIComponent(token)}`);
    const es = new EventSource(url);
    esRef.current = es;

    es.addEventListener('status', (e) => {
      const data = JSON.parse(e.data);
      if (data.status === 'starting') setStatus('starting');
    });

    es.addEventListener('qr', (e) => {
      const data = JSON.parse(e.data);
      setQrImage(data.qr);
      setStatus('qr');
    });

    es.addEventListener('connected', (e) => {
      const data = JSON.parse(e.data);
      setStatus('connected');
      setPhone(data.phoneNumber || '');
      setQrImage(null);
      es.close();
      onConnected(data.phoneNumber || '', data.displayName || '');
    });

    es.addEventListener('disconnected', () => {
      setStatus('disconnected');
      es.close();
    });

    es.addEventListener('error', (e) => {
      try {
        const data = JSON.parse((e as MessageEvent).data);
        setErrorMsg(data.message || 'Erreur de connexion');
      } catch {
        setErrorMsg('Connexion interrompue');
      }
      setStatus('error');
      es.close();
    });

    es.onerror = () => {
      if (status !== 'connected') {
        setStatus('error');
        setErrorMsg('Connexion au serveur perdue');
        es.close();
      }
    };
  };

  useEffect(() => {
    return () => { esRef.current?.close(); };
  }, []);

  return (
    <div className="flex flex-col items-center py-4">
      {/* État : idle */}
      {status === 'idle' && (
        <div className="text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Smartphone className="w-10 h-10 text-green-500" />
          </div>
          <p className="text-gray-600 text-sm mb-4 max-w-xs">
            Scannez le QR code avec votre téléphone pour connecter WhatsApp — comme WhatsApp Web.
          </p>
          <button
            onClick={startConnection}
            className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 mx-auto"
          >
            <Wifi className="w-4 h-4" /> Générer le QR Code
          </button>
        </div>
      )}

      {/* État : démarrage */}
      {status === 'starting' && (
        <div className="text-center py-6">
          <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Connexion à WhatsApp en cours...</p>
        </div>
      )}

      {/* État : QR affiché */}
      {status === 'qr' && qrImage && (
        <div className="text-center">
          <p className="text-sm font-medium text-gray-700 mb-3">
            Ouvrez WhatsApp sur votre téléphone → <strong>Appareils connectés</strong> → <strong>Connecter un appareil</strong>
          </p>
          <div className="inline-block p-3 bg-white border-2 border-gray-200 rounded-xl shadow-sm">
            <img src={qrImage} alt="QR Code WhatsApp" className="w-56 h-56" />
          </div>
          <p className="text-xs text-gray-400 mt-3">Le QR code expire toutes les 20 secondes — il se renouvelle automatiquement</p>
          <button
            onClick={startConnection}
            className="mt-3 flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mx-auto"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Regénérer
          </button>
        </div>
      )}

      {/* État : connecté */}
      {status === 'connected' && (
        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <p className="font-semibold text-green-700">WhatsApp connecté !</p>
          {phone && <p className="text-gray-500 text-sm mt-1">{phone}</p>}
        </div>
      )}

      {/* État : déconnecté ou erreur */}
      {(status === 'disconnected' || status === 'error') && (
        <div className="text-center">
          <p className="text-red-600 text-sm mb-3">
            {errorMsg || 'La connexion a été interrompue.'}
          </p>
          <button
            onClick={startConnection}
            className="flex items-center gap-2 px-5 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 mx-auto"
          >
            <RefreshCw className="w-4 h-4" /> Réessayer
          </button>
        </div>
      )}
    </div>
  );
}
